import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Button, Modal } from '@printdeal/ui-library';
import { hasReviewGroup, isReviewGroupLead } from '@printdeal/business-logic';

import { SanitizedLineItem, SanitizedOrder } from '../../../../../../types/order/Order';
import { DispatchCategoriesAccount } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import useReOrder from './useReOrder';

import ListInvalidProducts from './ListInvalidProducts';
import InstructionsReOrderAlert from './InstructionsReOrderAlert';
import SubmitReOrderButtons from './SubmitReOrderButtons';

interface ReOrderModalProps {
  lineItems: SanitizedLineItem[];
  order: SanitizedOrder;
  handleCloseModal: (eventName: string) => void;
  dispatchEventCategory?: DispatchCategoriesAccount;
}

const ReOrderModal = ({
  lineItems,
  order,
  handleCloseModal,
  dispatchEventCategory = 'order_detail_interaction',
}: ReOrderModalProps) => {
  const canReOrder = useReOrder(lineItems, order);
  const intl = useIntl();
  const countLineItems = canReOrder.validLineItems
    .filter((lineItem) => isReviewGroupLead(lineItem) || !hasReviewGroup(lineItem)).length;

  return (
    <Modal
      isOpen
      onClose={() => handleCloseModal('close_modal_reorder_line_item_generic')}
      dismissLabel={intl.formatMessage({
        id: 'account.general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'lineItems.list.reorder.title',
        defaultMessage: 'Reorder products',
      })}
      data-cy="modalReOrder"
    >
      <Modal.Content>
        <InstructionsReOrderAlert
          validLineItemsCount={countLineItems}
          hasValidFiles={canReOrder.hasValidFiles}
          hasInvalidFiles={canReOrder.hasInvalidFiles}
        />
        <Alert variant="info" className="pd-my-4">
          <p className="pd-mb-2">
            <FormattedMessage id="account.order.reorder.modal.edit.title" defaultMessage="Edit items?" />
          </p>
          <p>
            <FormattedMessage id="account.order.reorder.modal.edit.info" defaultMessage="Would you like to make changes to the product or design? Once added, you can make the necessary modifications from your shopping cart." />
          </p>
        </Alert>
        <ListInvalidProducts invalidLineItems={canReOrder.invalidLineItems} />
      </Modal.Content>
      <Modal.Footer
        className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2"
      >
        <SubmitReOrderButtons
          lineItems={canReOrder.validLineItems}
          hasValidFiles={canReOrder.hasValidFiles}
          order={order}
          dispatchEventCategory={dispatchEventCategory}
        />
        <Button
          variant="secondary"
          onClick={() => handleCloseModal('close_modal_reorder_by_button')}
          data-cy="cancelButtonReOrderItem"
        >
          <FormattedMessage
            id="account.order.reOrder.cancel.button"
            defaultMessage="Cancel"
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReOrderModal;
